if (!customElements.get('project-showcase')) {
  customElements.define('project-showcase', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.home-projects__track');
      this.cards = [...this.track.children];
      this.controls = this.querySelector('.home-projects__controls');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.controls.hidden = this.cards.length < 2;
      if (this.cards.length < 2) return;
      // Copies allow the two-card desktop layout to loop with only two projects.
      const copies = () => this.cards.map((card) => {
        const copy = card.cloneNode(true);
        copy.dataset.projectClone = '';
        copy.setAttribute('aria-hidden', 'true');
        [copy, ...copy.querySelectorAll('*')].forEach((element) => {
          element.removeAttribute('id');
          element.removeAttribute('data-shopify-editor-block');
        });
        copy.querySelectorAll('a, button, input, video, [tabindex]').forEach((element) => element.setAttribute('tabindex', '-1'));
        return copy;
      });
      this.track.prepend(...copies());
      this.track.append(...copies());
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-project-index')) {
          this.goTo(Number(button.dataset.projectIndex));
        } else if (button.hasAttribute('data-direction')) {
          this.goTo(this.index + Number(button.dataset.direction));
        }
      }, options);
      this.track.addEventListener('scroll', () => {
        this.update();
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settle(), 150);
      }, { ...options, passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || event.target.closest('video')) return;
        event.preventDefault();
        this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('a, button, video')) return;
        this.drag = { x: event.clientX, scroll: this.track.scrollLeft };
        this.track.setPointerCapture(event.pointerId);
        this.track.classList.add('home-projects__track--dragging');
        clearTimeout(this.autoplayTimer);
      }, options);
      this.track.addEventListener('pointermove', (event) => {
        if (this.drag) this.track.scrollLeft = this.drag.scroll - (event.clientX - this.drag.x);
      }, options);
      const endDrag = () => {
        if (!this.drag) return;
        this.drag = null;
        this.track.classList.remove('home-projects__track--dragging');
        this.update();
        this.settle();
        this.goTo(this.index);
      };
      this.track.addEventListener('pointerup', endDrag, options);
      this.track.addEventListener('pointercancel', endDrag, options);
      this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, options);
      this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, options);
      this.addEventListener('focusout', (event) => {
        this.focused = this.contains(event.relatedTarget);
        this.schedule();
      }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', () => this.schedule(), options);
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.cards.indexOf(event.target.closest('.home-projects__card'));
        if (index < 0) return;
        this.editorSelected = true;
        this.goTo(index, 'instant');
      }, options);
      this.addEventListener('shopify:block:deselect', () => {
        this.editorSelected = false;
        this.schedule();
      }, options);
      this.resizeObserver = new ResizeObserver(() => {
        this.step = this.cards[1].offsetLeft - this.cards[0].offsetLeft;
        this.jump(this.cards[0].offsetLeft + this.index * this.step);
      });
      this.resizeObserver.observe(this.track);
      this.schedule();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      clearTimeout(this.autoplayTimer);
      clearTimeout(this.settleTimer);
      cancelAnimationFrame(this.resetFrame);
      this.querySelectorAll('[data-project-clone]').forEach((copy) => copy.remove());
    }

    jump(left) {
      this.track.classList.add('home-projects__track--resetting');
      this.track.scrollTo({ left, behavior: 'instant' });
      cancelAnimationFrame(this.resetFrame);
      this.resetFrame = requestAnimationFrame(() => this.track.classList.remove('home-projects__track--resetting'));
      this.update();
    }

    goTo(index, behavior) {
      if (!this.step) return;
      clearTimeout(this.autoplayTimer);
      this.settle();
      const motion = behavior || (this.motion.matches ? 'instant' : 'smooth');
      this.track.scrollTo({ left: this.cards[0].offsetLeft + index * this.step, behavior: motion });
    }

    settle() {
      if (!this.step || this.drag) return;
      const start = this.cards[0].offsetLeft;
      const length = this.step * this.cards.length;
      const offset = this.track.scrollLeft - start;
      if (offset < -1 || offset >= length - 1) {
        this.jump(start + ((offset % length) + length) % length);
      }
      this.schedule();
    }

    update() {
      if (!this.step) return;
      const position = Math.round((this.track.scrollLeft - this.cards[0].offsetLeft) / this.step);
      this.index = ((position % this.cards.length) + this.cards.length) % this.cards.length;
      this.querySelectorAll('[data-project-index]').forEach((button, index) => {
        button.setAttribute('aria-current', String(index === this.index));
      });
    }

    schedule() {
      clearTimeout(this.autoplayTimer);
      if (this.dataset.autoplay !== 'true' || this.motion.matches || this.hovered || this.focused || this.drag || this.editorSelected || document.hidden) return;
      this.autoplayTimer = setTimeout(() => this.goTo(this.index + 1), 5000);
    }
  });
}
